import React from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { team, cabinetInfo } from '../../mock';

const splitName = (full) => {
  // "Dr. ZOUAOUI BOUNOUA Hadjira" → { title: "Dr.", lastName: "ZOUAOUI BOUNOUA", firstName: "Hadjira" }
  const tokens = full.split(' ');
  const title = tokens[0];
  const firstName = tokens[tokens.length - 1];
  const lastName = tokens.slice(1, -1).join(' ');
  return { title, lastName, firstName };
};

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function EquipeV2() {
  return (
    <section id="equipe" className="bg-muted/40 py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="mb-20 lg:mb-28">
          <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-foreground/55">
            04 — Notre équipe
          </span>
          <h2 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-foreground max-w-4xl">
            Trois dentistes, <span className="italic">une voix</span>.
          </h2>
          <p className="mt-6 max-w-2xl text-base lg:text-lg text-foreground/65 leading-relaxed">
            Omnipratique, implantologie, parodontologie, orthodontie. Un cabinet pluridisciplinaire
            sous un même toit pour un suivi cohérent à long terme.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 lg:gap-12">
          {team.map((member, idx) => {
            const parts = splitName(member.name);
            return (
              <motion.article
                key={member.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: idx * 0.1 }}
                className="group"
              >
                <div className="relative w-full aspect-[3/4] overflow-hidden rounded-md bg-foreground flex items-center justify-center">
                  <span aria-hidden="true" className="font-display text-[12rem] lg:text-[14rem] text-background leading-none">
                    {member.initials}
                  </span>
                  <span className="absolute top-4 left-4 inline-flex items-center px-3 py-1 rounded-full bg-amber-400 text-foreground text-[10px] font-bold uppercase tracking-[0.2em]">
                    {member.status === 'Titulaire' ? '★ Titulaire' : 'Collaborateur'}
                  </span>
                  <span className="absolute bottom-4 right-4 font-display text-5xl text-background/40 leading-none">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className="mt-6">
                  <h3 className="font-display text-3xl lg:text-4xl leading-[0.95] tracking-tight text-foreground">
                    {parts.title} {parts.lastName}
                    <br />
                    <span className="italic text-foreground/80">{parts.firstName}</span>
                  </h3>
                  <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-foreground/55">
                    {member.role}
                  </p>

                  <ul className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.2em] text-foreground/65">
                    {member.specialties.map((sp, i) => (
                      <li key={i} className="after:content-['·'] after:ml-3 after:text-foreground/30 last:after:content-['']">
                        {sp}
                      </li>
                    ))}
                  </ul>

                  <a
                    href={cabinetInfo.doctolib}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-foreground hover:text-amber-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
                    aria-label={`Prendre rendez-vous avec ${member.name}`}
                  >
                    <Calendar size={12} aria-hidden="true" />
                    Prendre RDV
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
